import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '../lib/supabase'
import type {
  MaintenanceSchedule,
  MaintenanceScheduleInsert,
  MaintenanceScheduleUpdate,
  MaintenanceEvent,
  MaintenanceEventInsert,
} from '../lib/database.types'
import { ACTIVITY_LOG_KEY, logActivity } from './useActivityLog'

const SCHEDULES_KEY = ['maintenance_schedules']
const EVENTS_KEY = ['maintenance_events']
const EXPENSES_KEY = ['expenses']

export const MAINTENANCE_TASK_TYPES = [
  'nozzle-swap',
  'belt-tension',
  'lube-rails',
  'hotend-clean',
  'bed-level',
  'firmware-update',
  'pei-replace',
  'other',
] as const

export const MAINTENANCE_INTERVAL_TYPES = ['hours', 'days', 'prints'] as const

export function useMaintenanceSchedules() {
  return useQuery({
    queryKey: SCHEDULES_KEY,
    queryFn: async (): Promise<MaintenanceSchedule[]> => {
      const { data, error } = await supabase
        .from('maintenance_schedules')
        .select('*')
        .order('printer_id', { ascending: true })
        .order('task_type', { ascending: true })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useMaintenanceEvents() {
  return useQuery({
    queryKey: EVENTS_KEY,
    queryFn: async (): Promise<MaintenanceEvent[]> => {
      const { data, error } = await supabase
        .from('maintenance_events')
        .select('*')
        .order('event_date', { ascending: false })
        .order('created_at', { ascending: false })
      if (error) throw error
      return data ?? []
    },
  })
}

export function useCreateSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Omit<MaintenanceScheduleInsert, 'user_id'>): Promise<MaintenanceSchedule> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')
      const { data, error } = await supabase
        .from('maintenance_schedules')
        .insert({ ...input, user_id: user.id })
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  })
}

export function useUpdateSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({ id, ...updates }: { id: string } & MaintenanceScheduleUpdate): Promise<MaintenanceSchedule> => {
      const { data, error } = await supabase
        .from('maintenance_schedules')
        .update(updates)
        .eq('id', id)
        .select('*')
        .single()
      if (error) throw error
      return data
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  })
}

export function useDeleteSchedule() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      const { error } = await supabase.from('maintenance_schedules').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: SCHEDULES_KEY }),
  })
}

export function useCreateMaintenanceEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: Omit<MaintenanceEventInsert, 'user_id'>): Promise<MaintenanceEvent> => {
      const { data: { user } } = await supabase.auth.getUser()
      if (!user) throw new Error('not authenticated')

      // Insert the event first, without linked_expense_id (we'll set it after).
      const { data: event, error } = await supabase
        .from('maintenance_events')
        .insert({ ...input, user_id: user.id, linked_expense_id: null })
        .select('*')
        .single()
      if (error) throw error

      // If a cost was logged, auto-create a matching expense row and link
      // it back to the event (honoring the "also logs an expense entry"
      // hint shown on the form).
      const cost = input.cost == null ? 0 : Number(input.cost)
      if (cost > 0) {
        const { data: expense, error: expErr } = await supabase
          .from('expenses')
          .insert({
            user_id: user.id,
            expense_date: input.event_date ?? new Date().toISOString().slice(0, 10),
            amount: cost,
            category: 'printer-part',
            printer_id: input.printer_id,
            description: `Maintenance: ${input.task_type}`,
          })
          .select('*')
          .single()
        if (expErr) {
          // Roll back the event so we don't leave a maintenance row with cost
          // but no expense. If the rollback itself fails, report both so the
          // user can clean up manually rather than seeing a single message.
          const { error: rbErr } = await supabase.from('maintenance_events').delete().eq('id', event.id)
          if (rbErr) {
            throw new Error(
              `Failed to create linked expense (${expErr.message}); rollback also failed (${rbErr.message}). Orphan maintenance_event id=${event.id} may need manual cleanup.`,
            )
          }
          throw expErr
        }
        const { error: linkErr } = await supabase
          .from('maintenance_events')
          .update({ linked_expense_id: expense.id })
          .eq('id', event.id)
        if (linkErr) throw linkErr
        event.linked_expense_id = expense.id
      }

      // Bump the schedule's last_done_* markers if this event was scheduled.
      if (input.schedule_id) {
        const updates: MaintenanceScheduleUpdate = {
          last_done_at: input.event_date ?? new Date().toISOString().slice(0, 10),
          ...(input.printer_hours_at_event != null
            ? { last_done_printer_hours: input.printer_hours_at_event }
            : {}),
        }
        await supabase
          .from('maintenance_schedules')
          .update(updates)
          .eq('id', input.schedule_id)
      }

      void logActivity({
        user_id: user.id,
        actor_user_id: user.id,
        entity_type: 'maintenance_event',
        entity_id: event.id,
        action: 'created',
        metadata: {
          task_type: input.task_type,
          cost,
          event_date: input.event_date ?? null,
        },
      })

      return event
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY })
      queryClient.invalidateQueries({ queryKey: SCHEDULES_KEY })
      queryClient.invalidateQueries({ queryKey: EXPENSES_KEY })
      queryClient.invalidateQueries({ queryKey: ACTIVITY_LOG_KEY })
    },
  })
}

export function useDeleteMaintenanceEvent() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (id: string): Promise<void> => {
      // Snapshot for the activity log + linked-expense cleanup.
      const { data: event, error: readErr } = await supabase
        .from('maintenance_events')
        .select('linked_expense_id, task_type, cost, user_id')
        .eq('id', id)
        .single()
      if (readErr) throw readErr
      if (event?.linked_expense_id) {
        // Throw on delete failure so we don't orphan the expense and
        // double-count it against profit. Linked expenses are part of the
        // cash-basis filament/maintenance picture.
        const { error: expDelErr } = await supabase
          .from('expenses')
          .delete()
          .eq('id', event.linked_expense_id)
        if (expDelErr) throw expDelErr
      }
      const { error } = await supabase.from('maintenance_events').delete().eq('id', id)
      if (error) throw error

      if (event) {
        void logActivity({
          user_id: event.user_id,
          actor_user_id: event.user_id,
          entity_type: 'maintenance_event',
          entity_id: id,
          action: 'deleted',
          metadata: { task_type: event.task_type, cost: event.cost },
        })
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: EVENTS_KEY })
      queryClient.invalidateQueries({ queryKey: EXPENSES_KEY })
      queryClient.invalidateQueries({ queryKey: ACTIVITY_LOG_KEY })
    },
  })
}

// Pure helper: compute due status for a schedule given current printer hours
export interface DueStatus {
  isDue: boolean
  isOverdue: boolean
  remaining: number  // in interval_type units
  label: string  // e.g. "Overdue by 20 hours" or "Due in 5 days"
}

export function computeDueStatus(
  schedule: MaintenanceSchedule,
  currentPrinterHours: number,
): DueStatus {
  const interval = schedule.interval_value
  let elapsed = 0
  if (schedule.interval_type === 'hours') {
    const lastHours = schedule.last_done_printer_hours ?? 0
    elapsed = currentPrinterHours - lastHours
  } else if (schedule.interval_type === 'days') {
    if (!schedule.last_done_at) {
      elapsed = interval + 1 // never done → treat as overdue
    } else {
      const lastDate = new Date(schedule.last_done_at)
      const now = new Date()
      elapsed = Math.floor((now.getTime() - lastDate.getTime()) / (1000 * 60 * 60 * 24))
    }
  } else if (schedule.interval_type === 'prints') {
    // prints-based not fully tracked here; treat as never overdue for now
    elapsed = 0
  }
  const remaining = interval - elapsed
  const isOverdue = remaining < 0
  const isDue = remaining <= 0
  const unit = schedule.interval_type === 'hours' ? 'h' : schedule.interval_type === 'days' ? ' days' : ' prints'
  const label = isOverdue
    ? `Overdue by ${Math.abs(remaining)}${unit}`
    : remaining === 0
      ? 'Due now'
      : `Due in ${remaining}${unit}`
  return { isDue, isOverdue, remaining, label }
}
